/**
 * Filter Event Handlers
 *
 * Handles filter chip interactions: favorite, closing today/week, high margin, needs price, active bids
 *
 * @module eventHandlers/filterHandlers
 */

import type { State } from '../types';

export interface FilterElements {
  filterFavorite?: HTMLElement;
  filterClosingToday?: HTMLElement;
  filterClosingWeek?: HTMLElement;
  filterHighMargin?: HTMLElement;
  filterNeedsPrice?: HTMLElement;
  filterActiveBids?: HTMLElement;
}

interface FilterHandlerDeps {
  state: State;
  render: () => void;
  showToast: (
    message: string,
    options?: { type?: string; variant?: string; duration?: number }
  ) => void;
}

/**
 * Toggle a filter chip on/off
 *
 * @param el - The chip element to toggle
 * @param flagKey - The filter key in state.filter
 * @param flagValue - The value to set when active
 * @param state - Application state
 * @param render - Render function
 */
function toggleChip(
  el: HTMLElement,
  flagKey: string,
  flagValue: string,
  state: State,
  render: () => void
): void {
  const isActive = el.getAttribute('data-active') === 'true';
  if (!state.filter) state.filter = {};

  if (isActive) {
    // Turning off
    (state.filter as Record<string, string>)[flagKey] = '';
    el.setAttribute('data-active', 'false');
    el.setAttribute('aria-pressed', 'false');
  } else {
    // Turning on
    (state.filter as Record<string, string>)[flagKey] = flagValue;
    el.setAttribute('data-active', 'true');
    el.setAttribute('aria-pressed', 'true');
  }

  state.page = 1;
  render();
}

/**
 * Setup favorite filter handler with dynamic UI sync
 *
 * @param el - Favorite filter element
 * @param deps - Dependencies
 */
async function setupFavoriteFilter(el: HTMLElement, deps: FilterHandlerDeps): Promise<void> {
  el.addEventListener('click', async () => {
    const isActive = el.getAttribute('data-active') === 'true';
    const newValue = isActive ? '' : 'only';

    if (!deps.state.filter) deps.state.filter = {};
    deps.state.filter.favorite = newValue;

    el.setAttribute('data-active', isActive ? 'false' : 'true');
    el.setAttribute('aria-pressed', isActive ? 'false' : 'true');
    deps.state.page = 1;

    if (newValue === 'only') {
      const { syncFavoriteOnlyUi } = await import('../modules/favoriteOnlyUi');
      syncFavoriteOnlyUi(true, { filterFavoriteEl: el, showToast: deps.showToast });
    } else {
      delete el.dataset.favoritesOnly;
    }

    deps.render();
  });
}

/**
 * Setup mutually exclusive closing filters (today/week)
 *
 * @param els - Filter elements
 * @param deps - Dependencies
 */
function setupClosingFilters(els: FilterElements, deps: FilterHandlerDeps): void {
  if (els.filterClosingToday) {
    els.filterClosingToday.addEventListener('click', () => {
      // Mutually exclusive with closing-week
      if (!deps.state.filter) deps.state.filter = {};
      if (deps.state.filter!.closing === 'week') {
        const other = els.filterClosingWeek as HTMLElement;
        other.setAttribute('data-active', 'false');
        other.setAttribute('aria-pressed', 'false');
      }
      toggleChip(els.filterClosingToday, 'closing', 'today', deps.state, deps.render);
    });
  }

  if (els.filterClosingWeek) {
    els.filterClosingWeek.addEventListener('click', () => {
      if (!deps.state.filter) deps.state.filter = {};
      if (deps.state.filter!.closing === 'today') {
        const other = els.filterClosingToday as HTMLElement;
        other.setAttribute('data-active', 'false');
        other.setAttribute('aria-pressed', 'false');
      }
      toggleChip(els.filterClosingWeek, 'closing', 'week', deps.state, deps.render);
    });
  }
}

/**
 * Setup all filter event handlers
 *
 * @param els - Filter elements
 * @param deps - Dependencies
 */
export function setupFilterHandlers(els: FilterElements, deps: FilterHandlerDeps): void {
  // Favorite filter (with special async handling)
  if (els.filterFavorite) {
    setupFavoriteFilter(els.filterFavorite, deps);
  }

  // Mutually exclusive closing filters
  setupClosingFilters(els, deps);

  // Independent filter chips
  if (els.filterHighMargin) {
    els.filterHighMargin.addEventListener('click', () => {
      toggleChip(els.filterHighMargin!, 'marginHigh', 'high', deps.state, deps.render);
    });
  }

  if (els.filterNeedsPrice) {
    els.filterNeedsPrice.addEventListener('click', () => {
      toggleChip(els.filterNeedsPrice!, 'needsPrice', 'needs', deps.state, deps.render);
    });
  }

  if (els.filterActiveBids) {
    els.filterActiveBids.addEventListener('click', () => {
      toggleChip(els.filterActiveBids!, 'bidding', 'active', deps.state, deps.render);
    });
  }
}
